import type { MouseEvent, ReactNode, Ref } from 'react'

export type LabeledOption<T = string> = { label: string; value: T }

export type ComboboxHandle = {
  clear: () => void
  focus: () => void
  blur: () => void
}

export type ComboboxClassNames<T> = {
  root?: string
  input?: string
  clear?: string
  chip?: string
  chipLabel?: string
  chipRemove?: string
  positioner?: string
  popup?: string
  list?: string
  option?: string | ((option: T) => string)
  trigger?: string
  empty?: string
  loading?: string
  create?: string
}

// Props shared by every variant (Combobox single, Combobox multi, Autocomplete).
export type SharedProps<T> = {
  ref?: Ref<ComboboxHandle>
  name?: string
  options: T[]
  getOptionLabel: (option: T) => string
  getOptionValue?: (option: T) => string
  isLoading?: boolean
  required?: boolean
  readOnly?: boolean
  disabled?: boolean
  placeholder?: string
  autoFocus?: boolean
  classNames?: ComboboxClassNames<T>
  portalToRoot?: boolean
  renderOption?: (option: T) => ReactNode
  emptyMessage?: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

// Combobox-only props (filter + input change). Autocomplete owns its input
// value via `value`/`onValueChange` instead.
export type ComboboxModeProps<T> = {
  onInputValueChange?: (value: string) => void
  filter?: ((option: T, query: string) => boolean) | null
}

export type SingleProps<T> = {
  multiple?: false
  value?: T | null
  onSelect?: (value: T | null) => void
  isClearable?: boolean
}

export type MultipleProps<T> = {
  multiple: true
  value: T[]
  onChange: (values: T[]) => void
  onCreate?: (inputValue: string) => void
  isValidNewOption?: (input: string) => boolean
  onKeyDown?: (e: React.KeyboardEvent) => void
}

export type ComboboxProps<T> = SharedProps<T> &
  ComboboxModeProps<T> &
  (SingleProps<T> | MultipleProps<T>)

export type AutocompleteProps<T> = SharedProps<T> & {
  itemToStringValue?: (option: T) => string
  value: string
  onValueChange: (value: string) => void
  onSelect?: (option: T) => void
  filter?: ((option: T, query: string) => boolean) | null
  isClearable?: boolean
}

export const preventMouseDown = (
  e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
) => e.preventDefault()

export const getOptionClassName = <T>(
  option: T,
  classNames?: ComboboxClassNames<T>,
): string | undefined => {
  if (typeof classNames?.option === 'function') {
    return classNames.option(option)
  }
  return classNames?.option
}
